/**
 * Updates medical order review status on the server.
 * @module updateOrderReviewStatus
 */

import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { throwApiError } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/** Body for the update-order-review-status POST. */
interface UpdateOrderReviewStatusPayload {
  order_id: string;
  medical_order_review_status: string;
  /** Sibling orders to apply the same review status to. Omit for a single order. */
  sibling_order_ids?: string[];
}

/**
 * POSTs the current medical order review status for an order.
 *
 * `org_id` is added here (not by callers) so the backend can seed its RLS
 * context, same as `postRequestForRefreshTemplates`.
 *
 * Failures follow the `/prior-auth-utilities/*` HTTP-status contract: 4xx/5xx
 * responses throw an `Error` enriched with `status` and `detail` (read by
 * `utils/showApiError` on the consumer side).
 *
 * @param payload - Order ID, new `medical_order_review_status` value, and optional siblings.
 * @returns Parsed JSON response from the server.
 * @throws {Error & { status: number; detail?: string }} On HTTP failure.
 * @throws {Error} When the bearer token is missing, or on network / parse failure.
 */
export const updateOrderReviewStatus = async (
  payload: UpdateOrderReviewStatusPayload,
) => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.UPDATE_ORDER_REVIEW_STATUS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...payload,
        org_id: getOrgIdForFetchExternalWorklist(),
      }),
    });

    if (!response.ok) {
      await throwApiError(response);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};
